import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAccessControl } from "@/hooks/useAccessControl";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Save, Info, AlertCircle } from "lucide-react";
import { catalogApi, type BarcodePattern } from "@/lib/api/catalog";

const BarcodePatternSetupPage = () => {
  const navigate = useNavigate();
  const { isTenantAdmin } = useAccessControl();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pattern, setPattern] = useState("");
  const [lastSequence, setLastSequence] = useState(0);
  const [preview, setPreview] = useState<string[]>([]);

  useEffect(() => {
    if (isTenantAdmin) {
      loadBarcodePattern();
    }
  }, [isTenantAdmin]);

  useEffect(() => {
    generatePreview();
  }, [pattern]);

  const loadBarcodePattern = async () => {
    try {
      setLoading(true);
      const data = await catalogApi.getBarcodePattern();
      setPattern(data.pattern);
      setLastSequence(data.lastSequence);
    } catch (error: any) {
      console.error("Failed to load barcode pattern:", error);
      alert(error?.response?.data?.message || "Failed to load barcode pattern");
    } finally {
      setLoading(false);
    }
  };

  const generatePreview = () => {
    if (!pattern) {
      setPreview([]);
      return;
    }

    const previews: string[] = [];
    const currentYear = new Date().getFullYear();
    const startSeq = lastSequence + 1;

    for (let i = 0; i < 5; i++) {
      const seq = startSeq + i;
      const previewBarcode = pattern
        .replace(/{seq}/g, seq.toString().padStart(6, "0"))
        .replace(/{year}/g, currentYear.toString())
        .replace(/{tenant}/g, ""); // Tenant code could be added if needed
      previews.push(previewBarcode);
    }

    setPreview(previews);
  };

  const handleSave = async () => {
    if (!pattern.trim()) {
      alert("Barcode pattern cannot be empty");
      return;
    }

    // Validate pattern contains at least {seq}
    if (!pattern.includes("{seq}")) {
      alert("Pattern must include {seq} placeholder to ensure unique barcodes");
      return;
    }

    try {
      setSaving(true);
      await catalogApi.updateBarcodePattern(pattern.trim());
      alert("Barcode pattern updated successfully");
      // Reload to get updated data
      await loadBarcodePattern();
    } catch (error: any) {
      console.error("Failed to update barcode pattern:", error);
      alert(error?.response?.data?.message || "Failed to update barcode pattern");
    } finally {
      setSaving(false);
    }
  };

  if (!isTenantAdmin) {
    return (
      <AppLayout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-4">Access Denied</h2>
            <p className="text-gray-600">You need Tenant Admin permissions to access this page.</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageContainer>
        <PageHeader
          title="Barcode Pattern Configuration"
          description="Configure how barcodes are generated for cataloged items. The pattern will be used for all new items."
          breadcrumbItems={[{ label: "Setup", href: "/setup" }, { label: "Barcode Pattern" }]}
          showSearch={false}
          titleSize="md"
        />

        {loading ? (
          <Card>
            <CardContent className="py-12">
              <LoadingSpinner size="lg" label="Loading barcode settings..." />
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Pattern Configuration */}
            <Card>
              <CardHeader>
                <CardTitle>Barcode Pattern</CardTitle>
                <CardDescription>
                  Define the format for automatically generated barcodes. Use placeholders to
                  customize the format.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="pattern">Pattern</Label>
                  <Input
                    id="pattern"
                    value={pattern}
                    onChange={(e) => setPattern(e.target.value)}
                    placeholder="GDL{seq}"
                    className="font-mono"
                  />
                  <p className="text-sm text-gray-500">
                    Current sequence: {lastSequence.toLocaleString()} (next barcode will be #
                    {lastSequence + 1})
                  </p>
                </div>

                {/* Placeholders Info */}
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    <div className="font-semibold mb-2">Available Placeholders:</div>
                    <ul className="list-disc list-inside space-y-1 text-sm">
                      <li>
                        <code className="bg-gray-100 px-1 py-0.5 rounded">{"{seq}"}</code> -
                        Sequential number (padded to 6 digits, e.g., 000001, 000002)
                      </li>
                      <li>
                        <code className="bg-gray-100 px-1 py-0.5 rounded">{"{year}"}</code> -
                        Current year (e.g., 2025)
                      </li>
                      <li>
                        <code className="bg-gray-100 px-1 py-0.5 rounded">{"{tenant}"}</code> -
                        Tenant code (if configured)
                      </li>
                    </ul>
                  </AlertDescription>
                </Alert>

                {/* Examples */}
                <div className="space-y-2">
                  <Label>Example Patterns:</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div
                      className="p-3 border rounded cursor-pointer hover:bg-gray-50"
                      onClick={() => setPattern("GDL{seq}")}
                    >
                      <div className="font-mono text-sm font-semibold">GDL{"{seq}"}</div>
                      <div className="text-xs text-gray-500 mt-1">Example: GDL000001</div>
                    </div>
                    <div
                      className="p-3 border rounded cursor-pointer hover:bg-gray-50"
                      onClick={() => setPattern("LIB-{year}-{seq}")}
                    >
                      <div className="font-mono text-sm font-semibold">
                        LIB-{"{year}"}-{"{seq}"}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">Example: LIB-2025-000001</div>
                    </div>
                    <div
                      className="p-3 border rounded cursor-pointer hover:bg-gray-50"
                      onClick={() => setPattern("BC{seq}")}
                    >
                      <div className="font-mono text-sm font-semibold">BC{"{seq}"}</div>
                      <div className="text-xs text-gray-500 mt-1">Example: BC000001</div>
                    </div>
                    <div
                      className="p-3 border rounded cursor-pointer hover:bg-gray-50"
                      onClick={() => setPattern("{tenant}-{seq}")}
                    >
                      <div className="font-mono text-sm font-semibold">{"{tenant}-{seq}"}</div>
                      <div className="text-xs text-gray-500 mt-1">Example: -000001</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Preview */}
            <Card>
              <CardHeader>
                <CardTitle>Preview</CardTitle>
                <CardDescription>
                  See how the next 5 barcodes will look with your pattern.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {preview.length > 0 ? (
                  <div className="space-y-2">
                    {preview.map((barcode, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded border"
                      >
                        <span className="font-mono font-semibold">{barcode}</span>
                        <span className="text-sm text-gray-500">
                          Sequence #{lastSequence + index + 1}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-gray-500 py-4">
                    Enter a pattern to see preview
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Important Notes */}
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <div className="font-semibold mb-2">Important Notes:</div>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>
                    Changing the pattern will only affect <strong>new</strong> barcodes generated
                    after the change.
                  </li>
                  <li>Existing barcodes will remain unchanged.</li>
                  <li>The sequence number continues from where it left off.</li>
                  <li>Make sure your pattern includes {"{seq}"} to ensure unique barcodes.</li>
                </ul>
              </AlertDescription>
            </Alert>

            {/* Actions */}
            <div className="flex justify-end gap-4">
              <Button variant="outline" onClick={() => navigate("/setup")}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving || !pattern.trim()}>
                {saving ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Pattern
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </PageContainer>
    </AppLayout>
  );
};

export default BarcodePatternSetupPage;
